import cn from 'classnames';
import { useAtomValue } from 'jotai';

import { useTranslate } from '@i18n';
import { useActivitySwings } from '@queries';
import { activeUserSettingsAtom, activityIdAtom, convertedLayoutCustomCorridorsMapAtom, corridorsAtom } from '@store';
import { u, ComparisonIcon, Box, Heading3 } from '@ui';

import { ComparisonCell, SwingCell } from './TableCell';
import * as css from './TableModuleGrid.css';
import { useTableParameters } from './useTableParameters';

export type CorridorMargins = {
    innerMax: number;
    innerMin: number;
};

type TableModuleGridProps = {
    nodeId: string;
    darkMode?: boolean;
    /**
     * When true, makes the header row and swing number column sticky
     * (top: 0, left: 0) for improved table navigation.
     */
    interactive?: boolean;
    displayTypography?: boolean;
};

export function TableModuleGrid({ darkMode, interactive, nodeId, displayTypography }: TableModuleGridProps) {
    const corridors = useAtomValue(corridorsAtom);
    const tableParameters = useTableParameters(nodeId);
    const t = useTranslate();
    const activityId = useAtomValue(activityIdAtom);
    const { data: swings } = useActivitySwings(activityId);
    const columns = tableParameters.length;
    const userSettings = useAtomValue(activeUserSettingsAtom);

    const layoutCustomCorridorsMap = useAtomValue(convertedLayoutCustomCorridorsMapAtom);
    if (tableParameters.length === 0 || swings?.length === 0) {
        return;
    }

    return (
        <div
            className={cn(
                css.table,
                darkMode ? css.tableDark : css.tableLight,
                interactive && css.interactive,
                displayTypography ? css.displayHeaderTypography : css.standardHeaderTypography,
            )}
            style={{ gridTemplateColumns: `${u(5)}px repeat(${columns}, minmax(120px, 170px))` }}
        >
            <Box className={css.row}>
                <div className={css.headerCell} />
                {tableParameters.map((parameter) => (
                    <div key={parameter.id} className={css.headerCell}>
                        {t(parameter.i18n ?? '-')}
                    </div>
                ))}
            </Box>
            {userSettings.showComparison && (
                <div className={css.row}>
                    <Box display="flex" justifyContent="center" alignItems="center" className={css.iconCell}>
                        <ComparisonIcon size={14} />
                    </Box>

                    {tableParameters.map((parameter, index) => (
                        <ComparisonCell
                            key={parameter.id}
                            measurementKey={parameter.parameter.name}
                            isLast={index === tableParameters.length - 1}
                        />
                    ))}
                </div>
            )}

            {/* This assumes order is always correct , i.e 5,4,3,2 */}
            {swings?.map((swing) => (
                <Box key={swing.number} className={css.row}>
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flex={1}
                        paddingInline={u(0.5)}
                        paddingBlock={u(1)}
                        className={css.swingNumberCell}
                    >
                        <Heading3>{swing.number}</Heading3>
                    </Box>

                    {tableParameters.map((uiParameter, index) => (
                        <SwingCell
                            key={uiParameter.id}
                            swingUUID={swing.uuid}
                            measurementKey={uiParameter.parameter.name}
                            isLast={index === tableParameters.length - 1}
                            goalCorridor={layoutCustomCorridorsMap?.[uiParameter.parameter.smpId]}
                            corridor={corridors?.[uiParameter.parameter.name]}
                        />
                    ))}
                </Box>
            ))}
        </div>
    );
}
